import React from 'react';
import './CrecheCadastroFields.css';
import api from '../services/api';

/** Uma opção de plano vinda de `GET /saas/planos-publicos`. */
interface PlanoPublicoOpcao {
  chave: string;
  nome: string;
  preco_mensal: number;
}

export interface CrecheCadastroData {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  telefone: string;
  cep: string;
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  // Responsável Legal — único: assina os contratos em nome da creche E é o
  // Gestor Master do sistema (o e-mail abaixo é o login).
  responsavelNome: string;
  responsavelEmail: string;
  responsavelCpf: string;
  responsavelRg: string;
  responsavelRgUf: string;
  responsavelNacionalidade?: string;
  responsavelEstadoCivil?: string;
  responsavelProfissao?: string;
  senha: string;
  confirmarSenha: string;
  plano: string;
}

export const CRECHE_CADASTRO_VAZIO: CrecheCadastroData = {
  cnpj: '',
  razaoSocial: '',
  nomeFantasia: '',
  telefone: '',
  cep: '',
  endereco: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  responsavelNome: '',
  responsavelEmail: '',
  responsavelCpf: '',
  responsavelRg: '',
  responsavelRgUf: '',
  responsavelNacionalidade: '',
  responsavelEstadoCivil: '',
  responsavelProfissao: '',
  senha: '',
  confirmarSenha: '',
  plano: 'BRONZE',
};

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
             'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

interface Props {
  value: CrecheCadastroData;
  onChange: (value: CrecheCadastroData) => void;
  mostrarSenha?: boolean;
  mostrarDocumentos?: boolean;
  mostrarPlano?: boolean;
  onBuscarCnpj: () => void;
  buscandoCnpj: boolean;
  erroCnpj?: string;
  onBuscarCep: () => void;
  buscandoCep: boolean;
  disabled?: boolean;
  mostrarAvisoConvite?: boolean;
}

/**
 * Campos de cadastro de Creche/Tenant — usados tanto no checkout público
 * (landing page) quanto no cadastro manual do admin SaaS. Mesma ordem,
 * mesmos rótulos, mesmo significado de campo nas duas telas.
 */
export default function CrecheCadastroFields({
  value, onChange, mostrarSenha = false, mostrarDocumentos = true, mostrarPlano = true,
  onBuscarCnpj, buscandoCnpj, erroCnpj,
  onBuscarCep, buscandoCep,
  disabled = false,
  mostrarAvisoConvite = true,
}: Props) {
  // Catálogo oficial de planos — a mesma fonte que a landing e a Liz usam.
  const [planos, setPlanos] = React.useState<PlanoPublicoOpcao[]>([]);

  React.useEffect(() => {
    if (!mostrarPlano) return;
    let ativo = true;
    api
      .get('/saas/planos-publicos')
      .then(r => { if (ativo) setPlanos(r.data || []); })
      .catch(() => { /* sem catálogo, o select fica vazio em vez de mentir preço */ });
    return () => { ativo = false; };
  }, [mostrarPlano]);

  function set<K extends keyof CrecheCadastroData>(campo: K, v: CrecheCadastroData[K]) {
    onChange({ ...value, [campo]: v });
  }

  return (
    <div className="ccf-root">
      <p className="ccf-section-title">Dados da Creche</p>

      <div className="ccf-row">
        <div className="ccf-group">
          <label>CNPJ (com busca automática)</label>
          <div className="ccf-inline">
            <input
              type="text"
              required
              disabled={disabled}
              placeholder="00.000.000/0000-00"
              value={value.cnpj}
              onChange={e => set('cnpj', e.target.value)}
            />
            <button type="button" className="ccf-btn-buscar" disabled={disabled || buscandoCnpj} onClick={onBuscarCnpj}>
              {buscandoCnpj ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
          {erroCnpj && <small className="ccf-erro">{erroCnpj}</small>}
        </div>
        <div className="ccf-group">
          <label>Razão Social</label>
          <input
            type="text"
            required
            disabled={disabled}
            placeholder="Ex: Creche Aquarela LTDA"
            value={value.razaoSocial}
            onChange={e => set('razaoSocial', e.target.value)}
          />
        </div>
      </div>

      <div className="ccf-row">
        <div className="ccf-group">
          <label>Nome Fantasia</label>
          <input
            type="text"
            disabled={disabled}
            placeholder="Ex: Creche Aquarela"
            value={value.nomeFantasia}
            onChange={e => set('nomeFantasia', e.target.value)}
          />
        </div>
        <div className="ccf-group">
          <label>Telefone</label>
          <input
            type="text"
            disabled={disabled}
            placeholder="(00) 0000-0000"
            value={value.telefone}
            onChange={e => set('telefone', e.target.value)}
          />
        </div>
      </div>

      <div className="ccf-row">
        <div className="ccf-group">
          <label>CEP (com busca automática)</label>
          <div className="ccf-inline">
            <input
              type="text"
              disabled={disabled}
              placeholder="00000-000"
              value={value.cep}
              onChange={e => set('cep', e.target.value)}
            />
            <button type="button" className="ccf-btn-buscar" disabled={disabled || buscandoCep} onClick={onBuscarCep}>
              {buscandoCep ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </div>
        <div className="ccf-group">
          <label>Logradouro</label>
          <input
            type="text"
            disabled={disabled}
            value={value.endereco}
            onChange={e => set('endereco', e.target.value)}
          />
        </div>
      </div>

      <div className="ccf-row">
        <div className="ccf-group">
          <label>Número</label>
          <input
            type="text"
            disabled={disabled}
            value={value.numero}
            onChange={e => set('numero', e.target.value)}
          />
        </div>
        <div className="ccf-group">
          <label>Complemento</label>
          <input
            type="text"
            disabled={disabled}
            value={value.complemento}
            onChange={e => set('complemento', e.target.value)}
          />
        </div>
      </div>

      <div className="ccf-row">
        <div className="ccf-group">
          <label>Bairro</label>
          <input
            type="text"
            disabled={disabled}
            value={value.bairro}
            onChange={e => set('bairro', e.target.value)}
          />
        </div>
        <div className="ccf-group">
          <label>Cidade / UF</label>
          <div className="ccf-inline">
            <input
              type="text"
              disabled={disabled}
              value={value.cidade}
              onChange={e => set('cidade', e.target.value)}
            />
            <select
              disabled={disabled}
              className="ccf-uf"
              value={value.estado}
              onChange={e => set('estado', e.target.value)}
            >
              <option value="">UF</option>
              {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>
        </div>
      </div>

      <p className="ccf-section-title">
        Responsável Legal (que assinará todos os contratos em nome da creche)
      </p>
      <p className="ccf-aviso">
        A mesma pessoa que responde juridicamente pela creche acessa o sistema como
        <strong> Gestor Master</strong> — o e-mail informado abaixo é o login.
      </p>

      <div className="ccf-row">
        <div className="ccf-group">
          <label>Nome completo</label>
          <input
            type="text"
            required
            disabled={disabled}
            placeholder="Ex: João Silva"
            value={value.responsavelNome}
            onChange={e => set('responsavelNome', e.target.value)}
          />
        </div>
        <div className="ccf-group">
          <label>E-mail (login do Gestor Master)</label>
          <input
            type="email"
            required
            disabled={disabled}
            placeholder="exemplo@creche.com.br"
            value={value.responsavelEmail}
            onChange={e => set('responsavelEmail', e.target.value)}
          />
        </div>
      </div>

      {mostrarDocumentos && (
        <>
          <div className="ccf-row">
            <div className="ccf-group">
              <label>CPF</label>
              <input
                type="text"
                required
                disabled={disabled}
                placeholder="000.000.000-00"
                value={value.responsavelCpf}
                onChange={e => set('responsavelCpf', e.target.value)}
              />
            </div>
            <div className="ccf-group">
              <label>RG / UF emissor</label>
              <div className="ccf-inline">
                <input
                  type="text"
                  required
                  disabled={disabled}
                  placeholder="Número do RG"
                  value={value.responsavelRg}
                  onChange={e => set('responsavelRg', e.target.value)}
                />
                <select
                  disabled={disabled}
                  className="ccf-uf"
                  value={value.responsavelRgUf}
                  onChange={e => set('responsavelRgUf', e.target.value.toUpperCase())}
                >
                  <option value="">UF</option>
                  {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Qualificação civil — obrigatória para os contratos gerados pela creche */}
          <div className="ccf-row">
            <div className="ccf-group">
              <label>Nacionalidade</label>
              <input
                type="text"
                disabled={disabled}
                placeholder="Ex: Brasileiro/Brasileira"
                value={value.responsavelNacionalidade || ''}
                onChange={e => set('responsavelNacionalidade', e.target.value)}
              />
            </div>
            <div className="ccf-group">
              <label>Estado Civil</label>
              <select
                disabled={disabled}
                value={value.responsavelEstadoCivil || ''}
                onChange={e => set('responsavelEstadoCivil', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Solteiro/a">Solteiro/a</option>
                <option value="Casado/a">Casado/a</option>
                <option value="Divorciado/a">Divorciado/a</option>
                <option value="Viúvo/a">Viúvo/a</option>
                <option value="Separado/a">Separado/a</option>
              </select>
            </div>
          </div>

          <div className="ccf-row">
            <div className="ccf-group">
              <label>Profissão</label>
              <input
                type="text"
                disabled={disabled}
                placeholder="Ex: Administrador/a, Empresário/a, etc"
                value={value.responsavelProfissao || ''}
                onChange={e => set('responsavelProfissao', e.target.value)}
              />
            </div>
          </div>
        </>
      )}

      {mostrarDocumentos && (mostrarSenha ? (
        <div className="ccf-row">
          <div className="ccf-group">
            <label>Senha de acesso</label>
            <input
              type="password"
              required
              disabled={disabled}
              placeholder="Mínimo 12 caracteres"
              value={value.senha}
              onChange={e => set('senha', e.target.value)}
            />
          </div>
          <div className="ccf-group">
            <label>Confirmar senha</label>
            <input
              type="password"
              required
              disabled={disabled}
              value={value.confirmarSenha}
              onChange={e => set('confirmarSenha', e.target.value)}
            />
          </div>
        </div>
      ) : (
        mostrarAvisoConvite && (
          <p className="ccf-aviso">
            O responsável receberá um e-mail de convite para definir a própria senha de acesso.
          </p>
        )
      ))}

      {mostrarPlano && (
        <>
          <p className="ccf-section-title">Plano</p>
          <div className="ccf-row">
            <div className="ccf-group">
              <label>Plano de interesse</label>
              <select
                disabled={disabled}
                value={value.plano}
                onChange={e => set('plano', e.target.value)}
              >
                {/* Nomes e preços vêm do catálogo do servidor: este dropdown
                    era mais um lugar com a sua própria cópia dos planos. */}
                {planos.map(p => (
                  <option key={p.chave} value={p.chave}>
                    Plano {p.nome} (R$ {p.preco_mensal}/mês)
                  </option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
